<template>
  <div
    :class="[
      'private-item-composer',
      { 'private-item-composer--hidden': !showDropdown && !showOverlay },
    ]"
  >
    <Transition name="fade-scale">
      <div
        v-if="showDropdown && !showOverlay"
        class="private-item-composer__menu"
        role="menu"
        aria-label="Choose an item type"
        @click.stop
      >
        <button
          class="private-item-composer__menu-item"
          type="button"
          role="menuitem"
          @click="openComposer('product')"
        >
          <svg class="tinyImg" aria-hidden="true">
            <use href="@/assets/icons/iconset.svg#product-tiny"></use>
          </svg>
          Product
        </button>
        <button
          class="private-item-composer__menu-item"
          type="button"
          role="menuitem"
          @click="openComposer('service')"
        >
          <svg class="tinyImg" aria-hidden="true">
            <use href="@/assets/icons/iconset.svg#service-tiny"></use>
          </svg>
          Service
        </button>
      </div>
    </Transition>

    <Overlay
      :visible="showOverlay"
      :title="composerTitle"
      :small-text-after-title="postingAsLabel"
      @close="closeComposer"
    >
      <template #before-buttons>
        <form
          id="private-item-publish-form"
          class="private-item-composer__form"
          @submit.prevent="publishItem"
        >
          <div>
            <label for="private-item-title">Name</label>
            <input
              id="private-item-title"
              v-model="title"
              type="text"
              maxlength="120"
              autocomplete="off"
              required
              :disabled="isSaving"
            />
          </div>

          <div>
            <label for="private-item-summary">Description</label>
            <textarea
              id="private-item-summary"
              v-model="summary"
              :maxlength="MAX_SUMMARY_LENGTH"
              required
              :disabled="isSaving"
            ></textarea>
          </div>

          <div>
            <label for="private-item-media">Photos (up to 8)</label>
            <input
              id="private-item-media"
              type="file"
              accept="image/avif,image/gif,image/jpeg,image/png,image/webp"
              multiple
              :disabled="isSaving"
              @change="selectFiles"
            />
            <p
              v-if="selectedFiles.length"
              class="smallText grayText nomargintopbottom"
            >
              {{ selectedFiles.length }}
              {{ selectedFiles.length === 1 ? "file" : "files" }} selected
            </p>
          </div>

          <div class="private-item-composer__listing-fields">
            <div>
              <label for="private-item-price">Price</label>
              <input
                id="private-item-price"
                v-model="priceInput"
                type="text"
                inputmode="decimal"
                autocomplete="off"
                placeholder="0.00"
                required
                :disabled="isSaving"
              />
            </div>
            <div v-if="kind === 'product'">
              <label for="private-item-stock">Available units</label>
              <input
                id="private-item-stock"
                v-model="stockInput"
                type="number"
                inputmode="numeric"
                min="1"
                max="1000000000"
                step="1"
                required
                :disabled="isSaving"
              />
            </div>
          </div>

          <fieldset class="private-item-composer__audience" :disabled="isSaving">
            <legend>Who can see it</legend>
            <label class="private-item-composer__audience-option">
              <input
                v-model="audience"
                type="radio"
                name="private-item-audience"
                value="public"
                data-private-item-audience="public"
              />
              <span>
                <strong>Everyone</strong>
                <span class="smallText grayText">
                  Shown in the open timeline and to visitors.
                </span>
              </span>
            </label>
            <label class="private-item-composer__audience-option">
              <input
                v-model="audience"
                type="radio"
                name="private-item-audience"
                value="organisation"
                data-private-item-audience="organisation"
              />
              <span>
                <strong>Members only</strong>
                <span class="smallText grayText">
                  Shown only to members of {{ orgName || "this organisation" }}.
                </span>
              </span>
            </label>
          </fieldset>

          <p class="smallText grayText nomargintopbottom">
            {{ kind === "service"
              ? "Services can be booked any number of times. The price is stored in the organisation currency."
              : "The price is stored in the organisation currency." }}
          </p>

          <p
            v-if="errorMessage"
            class="private-item-composer__error nomargintopbottom"
            role="alert"
          >
            {{ errorMessage }}
          </p>
          <p
            v-else-if="statusMessage"
            class="nomargintopbottom"
            role="status"
            aria-live="polite"
          >
            {{ statusMessage }}
          </p>
        </form>
      </template>

      <div class="row alignCentreRow">
        <UnitButton
          type="button"
          variant="secondary"
          :disabled="isSaving"
          @click="closeComposer"
        >
          Cancel
        </UnitButton>
        <UnitButton
          type="submit"
          form="private-item-publish-form"
          variant="primary"
          :loading="isSaving"
          loading-label="Publishing…"
          :disabled="isSaving"
        >
          Publish
        </UnitButton>
      </div>
    </Overlay>
  </div>
</template>

<script>
import { computed, ref } from "vue";

import Overlay from "@/components/GenericComponents/Overlay.vue";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";
import {
  createItemPublishingOperationBundle,
  itemPublishingAuthorityService,
} from "@/services/itemPublishingAuthorityService";
import { MAX_ITEM_SUMMARY_LENGTH } from "@/services/itemAuthorityService";
import { parseUnitAmountInputToAtomic } from "@/utils/atomicCurrencyFormatting";

const ORG_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;
const POSITIVE_INTEGER_PATTERN = /^[1-9][0-9]*$/;
// Services have no stock: the listing carries the largest stock the
// authority accepts (owner decision 2026-09-28), the same as migrated services.
const UNLIMITED_STOCK = 1_000_000_000;

export default {
  name: "PrivateItemComposer",
  components: { Overlay, UnitButton },
  props: {
    orgId: {
      type: String,
      required: true,
    },
    orgName: {
      type: String,
      default: "",
    },
    showDropdown: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["closeSellingAction", "itemPublished"],
  setup(props, { emit }) {
    const showOverlay = ref(false);
    const kind = ref("product");
    const title = ref("");
    const summary = ref("");
    const priceInput = ref("");
    const stockInput = ref("1");
    // Items are public by default (owner decision 2026-09-28).
    const audience = ref("public");
    const selectedFiles = ref([]);
    const isSaving = ref(false);
    const errorMessage = ref("");
    const statusMessage = ref("");
    const pendingDraft = ref(null);

    const composerTitle = computed(() =>
      kind.value === "service" ? "Add Service" : "Add Product"
    );
    const postingAsLabel = computed(() =>
      props.orgName
        ? `Publishing in ${props.orgName}`
        : "Publishing in the active organisation"
    );

    const resetDraft = () => {
      title.value = "";
      summary.value = "";
      priceInput.value = "";
      stockInput.value = "1";
      audience.value = "public";
      selectedFiles.value = [];
      errorMessage.value = "";
      statusMessage.value = "";
      pendingDraft.value = null;
    };

    const openComposer = (nextKind) => {
      if (!ORG_ID_PATTERN.test(props.orgId)) {
        errorMessage.value = "Select an organisation before publishing.";
        return;
      }
      kind.value = nextKind;
      showOverlay.value = true;
      emit("closeSellingAction");
    };

    const closeComposer = () => {
      if (isSaving.value) return;
      showOverlay.value = false;
      resetDraft();
      emit("closeSellingAction");
    };

    const selectFiles = (event) => {
      const files = Array.from(event?.target?.files || []);
      if (files.length > 8) {
        selectedFiles.value = [];
        errorMessage.value = "Choose no more than 8 photos.";
        if (event?.target) event.target.value = "";
        return;
      }
      if (files.some((file) => !file.type.startsWith("image/"))) {
        selectedFiles.value = [];
        errorMessage.value = "Items take photos only.";
        if (event?.target) event.target.value = "";
        return;
      }
      selectedFiles.value = files;
      errorMessage.value = "";
      pendingDraft.value = null;
    };

    const draftMatches = (current, next) =>
      current !== null &&
      current.orgId === next.orgId &&
      current.kind === next.kind &&
      current.title === next.title &&
      current.summary === next.summary &&
      current.audience === next.audience &&
      current.priceAtomic === next.priceAtomic &&
      current.availableStock === next.availableStock &&
      current.files.length === next.files.length &&
      current.files.every((file, index) => file === next.files[index]);

    const publishItem = async () => {
      if (isSaving.value) return;
      errorMessage.value = "";
      statusMessage.value = "";
      try {
        if (!ORG_ID_PATTERN.test(props.orgId)) {
          throw new TypeError("Select an organisation before publishing.");
        }
        const unlimited = kind.value === "service";
        if (!unlimited && !POSITIVE_INTEGER_PATTERN.test(stockInput.value)) {
          throw new TypeError(
            "Available units must be a positive whole number."
          );
        }
        const availableStock = unlimited
          ? UNLIMITED_STOCK
          : Number(stockInput.value);
        if (
          !Number.isSafeInteger(availableStock) ||
          availableStock > 1_000_000_000
        ) {
          throw new TypeError(
            "Available units are outside the supported range."
          );
        }
        const nextDraft = {
          orgId: props.orgId,
          kind: kind.value,
          title: title.value,
          summary: summary.value,
          audience: audience.value,
          priceAtomic: parseUnitAmountInputToAtomic(priceInput.value),
          availableStock,
          files: [...selectedFiles.value],
        };
        if (!draftMatches(pendingDraft.value, nextDraft)) {
          pendingDraft.value = Object.freeze({
            ...nextDraft,
            files: Object.freeze(nextDraft.files),
            operations: createItemPublishingOperationBundle(
              nextDraft.files.length
            ),
          });
        }

        isSaving.value = true;
        statusMessage.value = "Publishing through the private item authority…";
        const submittedOrgId = pendingDraft.value.orgId;
        const result = await itemPublishingAuthorityService.publishNewItem(
          pendingDraft.value
        );
        statusMessage.value = "Item published.";
        emit("itemPublished", { ...result, orgId: submittedOrgId });
        showOverlay.value = false;
        resetDraft();
        emit("closeSellingAction");
      } catch (error) {
        errorMessage.value =
          error instanceof TypeError
            ? error.message
            : "The item could not be published. Retry to resume the same operation.";
        statusMessage.value = "";
      } finally {
        isSaving.value = false;
      }
    };

    return {
      MAX_SUMMARY_LENGTH: MAX_ITEM_SUMMARY_LENGTH,
      audience,
      closeComposer,
      composerTitle,
      errorMessage,
      isSaving,
      kind,
      openComposer,
      postingAsLabel,
      priceInput,
      publishItem,
      selectFiles,
      selectedFiles,
      showOverlay,
      statusMessage,
      stockInput,
      summary,
      title,
    };
  },
};
</script>

<style scoped>
.private-item-composer--hidden {
  display: none;
}

.private-item-composer__menu {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  position: absolute;
  z-index: 20;
  padding: var(--space-3);
  border-radius: var(--radius-md);
  background: var(--color-surface-raised);
  box-shadow: var(--shadow-surface-raised);
}

.private-item-composer__menu-item {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-height: var(--control-height-default);
  padding: 0 var(--space-4);
  border: 0;
  border-radius: var(--radius-sm);
  color: var(--color-text-primary);
  background: transparent;
  font: inherit;
  text-align: start;
  cursor: pointer;
}

.private-item-composer__menu-item:hover,
.private-item-composer__menu-item:focus-visible {
  background: var(--color-accent-surface-subtle);
}

.private-item-composer__form {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  width: min(100%, 34rem);
}

.private-item-composer__form > div,
.private-item-composer__listing-fields > div {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.private-item-composer__listing-fields {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 12rem), 1fr));
  gap: var(--space-4);
}

.private-item-composer__audience {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin: 0;
  padding: 0;
  border: 0;
}

.private-item-composer__audience legend {
  margin-bottom: var(--space-2);
  padding: 0;
}

.private-item-composer__audience-option {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  margin: 0;
  text-align: left;
  cursor: pointer;
}

.private-item-composer__audience-option input {
  flex: 0 0 auto;
  width: var(--control-height-micro);
  height: var(--control-height-micro);
  min-height: 0;
  margin: 0;
}

.private-item-composer__audience-option > span {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.private-item-composer__error {
  color: var(--color-feedback-danger-foreground);
}

</style>
